<template>
  <div>
    <MaintenanceHeader />

    <div class="bg-ma-gray-11 lg:bg-white">
      <div class="container py-6 lg:py-20">
        <p class="text-xl text-center leading-looose lg:hidden">
          Downgrade to our Continuity Plan. We’ll save your data, and you’ll
          retain your license and access to your Collections, Plugins, Review
          and Portfolio.
        </p>

        <div class="mt-3 bg-white max-w-screen-lg mx-auto lg:-mt-40 lg:table lg:table-fixed lg:border-collapse">
          <div class="hidden lg:table-cell lg:align-top lg:w-1/2 lg:border">
            <div class="lg:bg-white lg:flex lg:flex-col lg:h-56 lg:p-4">
              <div class="text-ma-black-1 text-2xl leading-loose">
                Downgrade to our Continuity Plan. We’ll save your data, and
                you’ll retain your license and access to your Collections,
                Plugins, Review and Portfolio.
              </div>
            </div>

            <div class="h-16 py-3 px-6 flex border-t border-b">
              <span class="text-base text-ma-black-1 font-bold leading-tight">
                Key Features
              </span>
            </div>

            <div class="hidden pt-3 px-6 pb-8 text-base lg:block">
              <div class="flex items-center h-8">
                <SvgIcon icon="marketplace" class="" />
                <div class="mx-2 text-ma-black-1 font-medium">
                  Stock Media Library
                </div>
                <button
                  type="button"
                  @click="watchVideo(getAssetPath('explainer-reels/marketplace/preview-418-q0q1ZYCZlH-high.mp4'))"
                >
                  <SvgIcon icon="play" class="align-middle" />
                </button>
              </div>

              <div class="mt-5 flex items-center h-8">
                <SvgIcon icon="request" />
                <div class="mx-2 text-ma-black-1 font-medium">
                  Stock Media Requests
                </div>
              </div>

              <div class="mt-5 flex items-center h-8">
                <SvgIcon icon="plugins" class="" />
                <div class="mx-2 text-ma-black-1 font-medium">
                  Exclusive Plugins
                </div>
                <button
                  type="button"
                  class="js-dialog-video"
                  @click="watchVideo(getAssetPath('explainer-reels/plugins/MA_PluginsPromo_B01_2.mp4'))"
                >
                  <SvgIcon icon="play" class="align-middle" />
                </button>
              </div>

              <div class="mt-5 flex items-center h-8">
                <SvgIcon icon="review" class="" />
                <div class="mx-2 text-ma-black-1 font-medium">
                  Video Review Platform
                </div>
                <button
                  type="button"
                  class="js-dialog-video"
                  @click="watchVideo(getAssetPath('explainer-reels/reviews/Review+Dark+Mode.mp4'))"
                >
                  <SvgIcon icon="play" class="align-middle" />
                </button>
              </div>

              <div class="mt-5 flex items-center h-8">
                <SvgIcon icon="support" class="" />
                <div class="mx-2 text-ma-black-1 font-medium">
                  Customer Support
                </div>
              </div>

              <div class="mt-5 flex items-center h-8">
                <SvgIcon icon="storage" class="" />
                <div class="mx-2 text-ma-black-1 font-medium">
                  Review &amp; Upload Storage
                </div>
              </div>
            </div>
          </div>

          <div class="border lg:table-cell">
            <div class="h-56 p-6 flex flex-col">
              <div class="text-xl text-center text-ma-black-1 font-semibold leading-none">
                Continuity Plan
              </div>

              <div class="mt-3 text-center text-ma-gray-3 font-medium leading-tight">
                Save you licenses and data
              </div>

              <div class="mt-auto">
                <div class="text-center">
                  <div class="text-ma-black-1 font-bold leading-none">
                    <span class="text-3xl">${{priceBase}}</span>
                    <sup class="text-xl inline-flex">
                      <span class="relative text-ma-black-1 font-black" style="bottom: -4px;">
                        .{{priceFraction}}
                      </span>
                    </sup>
                    <span class="text-l" v-show="planPrice.tax_amount > 0">+ {{planPrice.tax_amount / 100 | CURRENCY}} VAT ({{planPrice.tax_rate}}%)</span>
                  </div>
                  <div class="mt-2 text-ma-gray-3 font-medium leading-normal">
                    Single User | Cancel Anytime
                  </div>
                </div>

                <button
                  type="button"
                  class="mt-3 ma-btn ma-btn--blue w-full px-2 lg:mt-4"
                  :class="{ 'opacity-25 cursor-default': submitting }"
                  :disabled="submitting"
                  @click="changePlan"
                >
                  Join The Continuity Plan
                </button>
              </div>
            </div>

            <div class="hidden h-16 px-4 items-center text-center text-ma-gray-3 text-base font-medium leading-tight border-t border-b lg:flex">
              Retain your asset’s license, save your data, and access to
              Collections, Plugins, Review and Portfolio.
            </div>

            <div class="pt-2 pb-8 text-base lg:pt-3">
              <div class="text-center">
                <div class="flex items-center justify-center lg:hidden">
                  <SvgIcon icon="marketplace" />
                  <div class="mx-2 text-ma-black-1 font-medium leading-9">
                    Stock Media Library
                  </div>
                  <button
                    type="button"
                    class="js-dialog-video"
                    @click="watchVideo(getAssetPath('explainer-reels/marketplace/preview-418-q0q1ZYCZlH-high.mp4'))"
                  >
                    <SvgIcon icon="play" class="align-middle" />
                  </button>
                </div>
                <div class="text-ma-gray-3 font-medium lg:text-ma-black-1 lg:leading-10 lg:h-8">
                  Previously Downloaded Asset License
                </div>
              </div>

              <div class="mt-5 text-center">
                <div class="flex items-center justify-center lg:hidden">
                  <SvgIcon icon="request" />
                  <div class="mx-2 text-ma-black-1 font-medium leading-9">
                    Stock Media Requests
                  </div>
                </div>
                <div class="text-ma-gray-3 font-medium lg:text-ma-black-1 lg:leading-10 lg:h-8">
                  Retain Access To Your Collections
                </div>
              </div>

              <div class="mt-5 text-center">
                <div class="flex items-center justify-center lg:hidden">
                  <SvgIcon icon="plugins" />
                  <div class="mx-2 text-ma-black-1 font-medium leading-9">
                    Exclusive Plugins
                  </div>
                  <button
                    type="button"
                    class="js-dialog-video"
                    @click="watchVideo(getAssetPath('explainer-reels/plugins/MA_PluginsPromo_B01_2.mp4'))"
                  >
                    <SvgIcon icon="play" class="align-middle" />
                  </button>
                </div>

                <div class="text-ma-gray-3 font-medium lg:text-ma-black-1 lg:leading-10 lg:h-8">
                  All Plugins Included
                </div>
              </div>

              <div class="mt-5 text-center">
                <div class="flex items-center justify-center lg:hidden">
                  <SvgIcon icon="review" />
                  <div class="mx-2 text-ma-black-1 font-medium leading-9">
                    Video Review Platform
                  </div>
                  <button
                    type="button"
                    class="js-dialog-video"
                    @click="watchVideo(getAssetPath('explainer-reels/reviews/Review+Dark+Mode.mp4'))"
                  >
                    <SvgIcon icon="play" class="align-middle" />
                  </button>
                </div>
                <div class="text-ma-gray-3 font-medium lg:text-ma-black-1 lg:leading-10 lg:h-8">
                  Video Review Platform Included
                </div>
              </div>

              <div class="mt-5 text-center">
                <div class="flex items-center justify-center lg:hidden">
                  <SvgIcon icon="support" />
                  <div class="mx-2 text-ma-black-1 font-medium leading-9">
                    Customer Support
                  </div>
                </div>
                <div class="text-ma-gray-3 font-medium lg:text-ma-black-1 lg:leading-10 lg:h-8">
                  Standard Customer Support
                </div>
              </div>

              <div class="mt-5 text-center">
                <div class="flex items-center justify-center lg:hidden">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="17"
                    fill="none"
                  >
                    <path
                      stroke="#67C7D8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.89"
                      d="M15.58 8.5H1.42M.94 8.5l2.92-4.88a1.42 1.42 0 011.27-.79h6.74a1.42 1.42 0 011.27.79l2.92 4.88v4.72c0 .52-.43.95-.95.95H1.9a.94.94 0 01-.95-.95V8.5zM4.25 11.33h0M7.08 11.33h.01"
                    />
                  </svg>
                  <div class="mx-2 text-ma-black-1 font-medium leading-9">
                    Review &amp; Upload Storage
                  </div>
                </div>
                <div class="text-ma-gray-3 font-medium lg:text-ma-black-1 lg:leading-10 lg:h-8">
                  250GB
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-8 max-w-screen-sm mx-auto space-y-2 lg:flex lg:space-y-0 lg:space-x-4">
          <a
            href="/account/subscription"
            class="ma-btn ma-btn--o w-full text-ma-gray-3 hover:bg-ma-gray-2 hover:text-ma-black-1"
          >
            Keep My Current Plan
          </a>

          <button
            type="button"
            class="ma-btn ma-btn--o w-full text-ma-gray-3 hover:bg-ma-gray-2 hover:text-ma-black-1"
            @click="downgradeHandler"
          >
            Downgrade To Free &amp; Delete My Data
          </button>
        </div>
      </div>
    </div>

    <Modal
      event-tracking-source="adobe extension video popup"
      v-if="showModal"
      @close="showModal = false"
    >
      <template #header>
        <button
          type="button"
          class="modal__close"
          @click="showModal = false"
        >
          &times;
        </button>
      </template>

      <div class="relative" style="padding-top: calc(56.25% + 40px);">
        <div class="absolute inset-0">
          <video ref="video" class="video-js"></video>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import axios from 'axios'
import SvgIcon from "../../../components/SvgIcon"
import MaintenanceHeader from './MaintenanceHeader'
import Modal from '../../../components/Modal'

import { getAssetPath } from '@/utils/contentDelivery';

export default {
  metaInfo: () => ({
      link: [
        { rel: "stylesheet", href: "https://vjs.zencdn.net/7.7.6/video-js.css" },
        { rel: "stylesheet", href: "/assets/site/css/maintenance.css" },
      ]
  }),

  props: {
    downgradeHandler: {
      required: true,
      type: Function
    }
  },

  components: {
    SvgIcon,
    MaintenanceHeader,
    Modal
  },

  data() {
    return {
      submitting: false,
      showModal: false,
      planPrice: {
        tax_rate: 0,
        amount: 999,
        tax_amount: 0,
      }
    }
  },

  mounted() {
    this.getPlanPrice()
  },

  computed: {
    priceBase() {
      return Math.floor(this.planPrice.amount / 100);
    },
    priceFraction() {
      let base = Math.floor(this.planPrice.amount / 100);
      return this.planPrice.amount - base * 100;
    }
  },

  methods: {
    changePlan() {
      if (this.submitting) {
        return
      }

      this.submitting = true

      return axios.post('/account/subscription/change', {
        billing_id: 'monthly_maintenance_2020'
      })
      .then((response) => {
        window.location = '/account/subscription/?downgrade=true&plan=monthly_maintenance_2020'
      })
      .catch((error) => {
        this.submitting = false
        console.error(error)
      })
    },

    getPlanPrice() {
      axios.get(`/api/subscription/downgrade/stripe/calculate-maintenance-plan-amount`)
              .then((response) => {
                this.planPrice = response.data;
              })
              .catch((error) => {
                //TODO: This message should redirect user back.
                console.log(error.response.data.message);
              });
    },

    watchVideo(src) {
      this.showModal = true

      this.$nextTick(() => {
        const vjs = window.videojs(this.$refs.video, {
          fluid: true,
          playsinline: true,
          inactivityTimeout: 0,
          controls: true,
          controlBar: {
            children: [
              "playToggle",
              "progressControl",
              "currentTimeDisplay",
              "timeDivider",
              "durationDisplay",
              "volumePanel",
              "fullscreenToggle",
            ],
            playToggle: {
              replay: false,
            },
            volumePanel: {
              inline: false,
            },
          },
        });

        vjs.src(src)
        vjs.play()
      })
    },

    getAssetPath
  }
};
</script>

<style scoped>
.modal-container {
  @apply fixed m-0 p-0 w-4/5;
  top: 50%;
  left: 50%;
  max-width: 1100px;
}

.modal__close {
  position: absolute;
  top: 40px;
  right: 40px;
  z-index: 10;
  font-weight: 700;
  font-size: 36px;
  line-height: 1;
  text-shadow: 0 1px 0 #fff;
  opacity: 0.4;
}
</style>
